import { normalizeQuestionnaireData } from '../utils/questionnairePages';

class FragebogenService {
    constructor() {
        this.storageKey = 'hr_dashboard_fragebogen';
    }

    // Save fragebogen data for a campaign
    saveFragebogen(campaignId, fragebogenData) {
        try {
            const normalizedFragebogen = normalizeQuestionnaireData(fragebogenData);
            const fragebogenzes = this.getAllFragebogenzes();
            fragebogenzes[campaignId] = {
                ...normalizedFragebogen,
                id: normalizedFragebogen.id || `fragebogen_${campaignId}_${Date.now()}`,
                campaignId,
                createdAt: normalizedFragebogen.createdAt || new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };
            localStorage.setItem(this.storageKey, JSON.stringify(fragebogenzes));
            return { success: true, data: fragebogenzes[campaignId] };
        } catch (error) {
            console.error('Error saving fragebogen:', error);
            return { success: false, error: error.message };
        }
    }

    // Get fragebogen data for a campaign
    getFragebogen(campaignId) {
        try {
            const fragebogenzes = this.getAllFragebogenzes();
            return { success: true, data: fragebogenzes[campaignId] || null };
        } catch (error) {
            console.error('Error getting fragebogen:', error);
            return { success: false, error: error.message };
        }
    }

    // Get all fragebogenzes
    getAllFragebogenzes() {
        try {
            const stored = localStorage.getItem(this.storageKey);
            return stored ? JSON.parse(stored) : {};
        } catch (error) {
            console.error('Error getting all fragebogenzes:', error);
            return {};
        }
    }

    // Delete fragebogen for a campaign
    deleteFragebogen(campaignId) {
        try {
            const fragebogenzes = this.getAllFragebogenzes();
            delete fragebogenzes[campaignId];
            localStorage.setItem(this.storageKey, JSON.stringify(fragebogenzes));
            return { success: true };
        } catch (error) {
            console.error('Error deleting fragebogen:', error);
            return { success: false, error: error.message };
        }
    }

    // Check if campaign has a fragebogen
    hasFragebogen(campaignId) {
        const fragebogen = this.getFragebogen(campaignId);
        return fragebogen.success && fragebogen.data !== null;
    }

    // Save fragebogen submission
    saveSubmission(campaignId, submission) {
        try {
            const submissionsKey = `hr_dashboard_submissions_${campaignId}`;
            const submissions = this.getSubmissions(campaignId);
            const newSubmission = {
                ...submission,
                id: `submission_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                campaignId,
                submittedAt: new Date().toISOString()
            };
            submissions.push(newSubmission);
            localStorage.setItem(submissionsKey, JSON.stringify(submissions));
            return { success: true, data: newSubmission };
        } catch (error) {
            console.error('Error saving submission:', error);
            return { success: false, error: error.message };
        }
    }

    // Get all submissions for a campaign
    getSubmissions(campaignId) {
        try {
            const submissionsKey = `hr_dashboard_submissions_${campaignId}`;
            const stored = localStorage.getItem(submissionsKey);
            return stored ? JSON.parse(stored) : [];
        } catch (error) {
            console.error('Error getting submissions:', error);
            return [];
        }
    }

    // Export fragebogen data as JSON
    exportFragebogen(campaignId) {
        try {
            const fragebogen = this.getFragebogen(campaignId);
            if (fragebogen.success && fragebogen.data) {
                const dataStr = JSON.stringify(fragebogen.data, null, 2);
                const dataBlob = new Blob([dataStr], { type: 'application/json' });
                const url = URL.createObjectURL(dataBlob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `fragebogen_${campaignId}_${new Date().toISOString().split('T')[0]}.json`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                return { success: true };
            } else {
                return { success: false, error: 'Fragebogen not found' };
            }
        } catch (error) {
            console.error('Error exporting fragebogen:', error);
            return { success: false, error: error.message };
        }
    }

    // Import fragebogen data from JSON
    importFragebogen(campaignId, fragebogenData) {
        try {
            // Validate fragebogen data structure
            if (!fragebogenData.title || (!Array.isArray(fragebogenData.questions) && !Array.isArray(fragebogenData.questionPages))) {
                return { success: false, error: 'Invalid fragebogen data format' };
            }

            const result = this.saveFragebogen(campaignId, fragebogenData);
            return result;
        } catch (error) {
            console.error('Error importing fragebogen:', error);
            return { success: false, error: error.message };
        }
    }

    // Get fragebogen statistics
    getFragebogenStats(campaignId) {
        try {
            const fragebogen = this.getFragebogen(campaignId);
            const submissions = this.getSubmissions(campaignId);
            
            if (!fragebogen.success || !fragebogen.data) {
                return { success: false, error: 'Fragebogen not found' };
            }

            const stats = {
                totalQuestions: fragebogen.data.questions.length,
                totalSubmissions: submissions.length,
                averageCompletionTime: 0, // Could be calculated if we track timing
                questionStats: fragebogen.data.questions.map(question => ({
                    questionId: question.id,
                    questionText: question.text,
                    type: question.type,
                    totalAnswers: 0,
                    answerDistribution: {}
                }))
            };

            // Calculate answer distributions
            submissions.forEach(submission => {
                Object.entries(submission.answers || {}).forEach(([questionId, answer]) => {
                    const questionStat = stats.questionStats.find(q => q.questionId === questionId);
                    if (questionStat) {
                        questionStat.totalAnswers++;
                        
                        if (Array.isArray(answer)) {
                            // Multiple choice
                            answer.forEach(optionIndex => {
                                const key = `option_${optionIndex}`;
                                questionStat.answerDistribution[key] = (questionStat.answerDistribution[key] || 0) + 1;
                            });
                        } else if (typeof answer === 'number') {
                            // Single choice
                            const key = `option_${answer}`;
                            questionStat.answerDistribution[key] = (questionStat.answerDistribution[key] || 0) + 1;
                        } else if (typeof answer === 'string') {
                            // Free text
                            questionStat.answerDistribution.freeText = (questionStat.answerDistribution.freeText || 0) + 1;
                        }
                    }
                });
            });

            return { success: true, data: stats };
        } catch (error) {
            console.error('Error getting fragebogen stats:', error);
            return { success: false, error: error.message };
        }
    }

    // Clear all data (for testing/development)
    clearAllData() {
        try {
            localStorage.removeItem(this.storageKey);
            // Clear all submission data
            const keys = Object.keys(localStorage);
            keys.forEach(key => {
                if (key.startsWith('hr_dashboard_submissions_')) {
                    localStorage.removeItem(key);
                }
            });
            return { success: true };
        } catch (error) {
            console.error('Error clearing all data:', error);
            return { success: false, error: error.message };
        }
    }
}

// Create and export a singleton instance
const fragebogenService = new FragebogenService();
export default fragebogenService;
